import { ArrowDown } from 'lucide-react';

import {
  AccreditationsMemberships,
  CodeOfConduct,
  ContactUs,
  EduQuaCertification,
  InternationalFaculty,
  LeadershipBoard,
  QualityAssurance,
  SocialResponsibility,
  VisionMission,
} from '@/components/about';
import {
  MediaFrame,
  NavyAtmosphere,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { Button } from '@ibas/ui';

/**
 * Real IBAS campus asset. A still is used deliberately — the About page carries
 * its motion through the sections rather than an autoplaying hero loop.
 */
const HERO_POSTER = '/media/images/campus/campus-building-exterior.webp';

/**
 * AboutPage (/about) — "About IBAS". A navy, motion-led hero opens the page, then
 * the nine requested blocks follow in order: Vision & Mission · Management &
 * International Advisory Board · International Faculty · Accreditations &
 * Memberships · EduQua ★ · Quality Assurance ★ · Social Responsibility ★ ·
 * Code of Conduct ★ · Contact Us ★. The shared enquiry form closes the page.
 *
 * Five sections carry accreditation-mandated verbatim copy (EduQua, Quality
 * Assurance / PDCA, Social Responsibility, Code of Conduct, Contact). Each
 * in-page section is anchored (id + scroll-mt) so the mega-nav can deep-link to
 * it. Every section is a self-contained, reduced-motion-safe component reading
 * only verified facts from @/data/about.
 */
export function AboutPage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="About IBAS"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              A Swiss academy, built for a global career.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">
                The International Business Academy of Switzerland (IBAS) is a private, international
                institution in Thalwil, near Zurich. Meet the people, the accreditations and the
                commitments behind our promise of quality education, affordable and accessible for
                all.
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  asChild
                >
                  <a href="#accreditations">
                    See our accreditations
                    <ArrowDown aria-hidden className="size-4" />
                  </a>
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Hero media plate — pointer parallax over a tilt-reactive frame. */}
          <PointerParallax strength={16} className="mx-auto w-full max-w-md lg:max-w-none">
            <TiltCard className="rounded-2xl" glare>
              <div data-parallax data-depth="0.8">
                <MediaFrame
                  image={HERO_POSTER}
                  alt="Exterior view of a modern copper-clad multi-storey IBAS building with a landscaped entrance."
                  aspect="aspect-[4/5]"
                  overlay={55}
                  rounded="rounded-2xl"
                  position="50% 40%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Quality Education. Affordable and Accessible for All.
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      International by design.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </TiltCard>
          </PointerParallax>
        </div>
      </section>

      {/* ── 1 · VISION & MISSION ──────────────────────────────────────────── */}
      <div id="vision-mission" className="scroll-mt-24">
        <VisionMission />
      </div>

      {/* ── 2 · MANAGEMENT & INTERNATIONAL ADVISORY BOARD ─────────────────── */}
      <div id="leadership" className="scroll-mt-24">
        <LeadershipBoard />
      </div>

      {/* ── 3 · INTERNATIONAL FACULTY ─────────────────────────────────────── */}
      <div id="faculty" className="scroll-mt-24">
        <InternationalFaculty />
      </div>

      {/* ── 4 · ACCREDITATIONS & MEMBERSHIPS ──────────────────────────────── */}
      <div id="accreditations" className="scroll-mt-24">
        <AccreditationsMemberships />
      </div>

      {/* ── 5 · EDUQUA ★ (text only — no logo) ────────────────────────────── */}
      <div id="eduqua" className="scroll-mt-24">
        <EduQuaCertification />
      </div>

      {/* ── 6 · QUALITY ASSURANCE ★ ───────────────────────────────────────── */}
      <div id="quality" className="scroll-mt-24">
        <QualityAssurance />
      </div>

      {/* ── 7 · SOCIAL RESPONSIBILITY ★ ───────────────────────────────────── */}
      <div id="social-responsibility" className="scroll-mt-24">
        <SocialResponsibility />
      </div>

      {/* ── 8 · CODE OF CONDUCT ★ ─────────────────────────────────────────── */}
      <div id="code-of-conduct" className="scroll-mt-24">
        <CodeOfConduct />
      </div>

      {/* ── 9 · CONTACT US ★ ──────────────────────────────────────────────── */}
      <div id="contact" className="scroll-mt-24">
        <ContactUs />
      </div>

      {/* Shared enquiry form closes the page. */}
      <EnquiryForm />
    </div>
  );
}
